import React, { createContext, useContext, useEffect, useState } from 'react';
import { useOpenLayersContext } from '../OpenLayersContext';

interface MapStateContextProps {
  currentZoom: number;
}

const MapStateContext = createContext<MapStateContextProps | undefined>(
  undefined
);

interface MapStateProviderProps {
  children: React.ReactNode;
}

export const MapStateProvider = ({ children }: MapStateProviderProps) => {
  const { map } = useOpenLayersContext();
  const [currentZoom, setCurrentZoom] = useState(
    map?.getView()?.getZoom() ?? 0
  );

  useEffect(() => {
    if (!map) return;

    // Function to update zoom state
    const handleZoomChange = () => {
      const newZoom = map.getView().getZoom();
      if (newZoom === null || newZoom === undefined) {
        return;
      }
      setCurrentZoom(newZoom);
    };

    // Listen for map view changes
    map.getView().on('change:resolution', handleZoomChange);

    // Initial zoom
    handleZoomChange();

    // Clean up listener when component unmounts or map changes
    return () => {
      map.getView().un('change:resolution', handleZoomChange);
    };
  }, [map]);

  return (
    <MapStateContext.Provider value={{ currentZoom }}>
      {children}
    </MapStateContext.Provider>
  );
};

export const useMapState = () => {
  const context = useContext(MapStateContext);

  if (context === undefined) {
    throw new Error('useMapState must be used within a MapStateProvider');
  }

  return context;
};
